import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, PencilSimple, Trash, Folder, FolderPlus, House, UsersThree } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Routines() {
  const navigate = useNavigate();
  const [routines, setRoutines] = useState([]);
  const [groups, setGroups] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [showProgramForm, setShowProgramForm] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [r, g, p] = await Promise.all([
        axios.get(`${API_URL}/routines`, { withCredentials: true }),
        axios.get(`${API_URL}/groups`, { withCredentials: true }),
        axios.get(`${API_URL}/programs`, { withCredentials: true }),
      ]);
      setRoutines(r.data);
      setGroups(g.data);
      setPrograms(p.data);
    } finally {
      setLoading(false);
    }
  };

  const createProgram = async (name) => {
    await axios.post(`${API_URL}/programs`, { name }, { withCredentials: true });
    await loadAll();
    setShowProgramForm(false);
  };

  const deleteRoutine = async (id) => {
    if (!window.confirm('Delete this routine?')) return;
    await axios.delete(`${API_URL}/routines/${id}`, { withCredentials: true });
    await loadAll();
  };

  const deleteProgram = async (id) => {
    if (!window.confirm('Delete this program? Routines will be kept but ungrouped.')) return;
    await axios.delete(`${API_URL}/programs/${id}`, { withCredentials: true });
    await loadAll();
  };

  if (loading) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center">
        <div className="animate-pulse-soft text-6xl font-black font-heading text-graphite">B</div>
      </div>
    );
  }

  const routinesByProgram = {};
  const unassigned = [];
  routines.forEach((r) => {
    if (r.program_id) {
      (routinesByProgram[r.program_id] = routinesByProgram[r.program_id] || []).push(r);
    } else {
      unassigned.push(r);
    }
  });

  return (
    <div className="mobile-container min-h-screen">
      <div className="content-wrapper px-5 pt-10">
        <div className="flex items-baseline justify-between mb-6 animate-slide-up">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-graphite/50 font-semibold mb-1">
              Library
            </p>
            <h1 className="text-5xl font-black font-heading uppercase tracking-tight text-graphite leading-none">
              Routines
            </h1>
          </div>
          <button
            onClick={() => navigate('/routine/new')}
            className="h-12 px-4 bg-userRed text-bone font-bold text-xs uppercase tracking-[0.15em] rounded-ios-pill flex items-center gap-1.5"
            data-testid="new-routine-button"
          >
            <Plus size={16} weight="bold" />
            New
          </button>
        </div>

        {/* Programs section */}
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-lg font-black font-heading uppercase text-graphite/80">Programs</h2>
          <button
            onClick={() => setShowProgramForm(true)}
            className="text-xs uppercase tracking-[0.15em] font-bold text-graphite/60 hover:text-graphite flex items-center gap-1"
            data-testid="new-program-button"
          >
            <FolderPlus size={14} weight="bold" /> New Program
          </button>
        </div>

        {programs.length === 0 && (
          <p className="text-sm text-graphite/50 mb-6">
            Group routines into programs — e.g. "Week 1" or "Push/Pull/Legs".
          </p>
        )}

        {programs.map((prog) => (
          <ProgramSection
            key={prog.id}
            program={prog}
            routines={routinesByProgram[prog.id] || []}
            groups={groups}
            onDelete={() => deleteProgram(prog.id)}
            onDeleteRoutine={deleteRoutine}
            onStart={(r) => startWorkout(r, navigate)}
          />
        ))}

        {/* Unassigned routines */}
        <div className="mt-8 mb-3">
          <h2 className="text-lg font-black font-heading uppercase text-graphite/80">
            {programs.length === 0 ? 'All Routines' : 'Ungrouped'}
          </h2>
        </div>

        {unassigned.length === 0 && programs.length === 0 && (
          <div className="ios-card p-8 text-center">
            <h3 className="text-xl font-black font-heading uppercase text-graphite mb-2">
              No Routines Yet
            </h3>
            <p className="text-sm text-graphite/60 mb-6">
              Build your first one — solo or shared with a squad.
            </p>
            <button
              onClick={() => navigate('/routine/new')}
              className="h-14 px-8 bg-userRed text-bone font-black text-sm uppercase tracking-wider rounded-ios-pill"
              data-testid="empty-create-routine-button"
            >
              Create Routine
            </button>
          </div>
        )}

        <div className="space-y-3">
          {unassigned.map((r) => (
            <RoutineListRow
              key={r.id}
              routine={r}
              group={groups.find((g) => g.id === r.group_id)}
              onEdit={() => navigate(`/routine/${r.id}/edit`)}
              onDelete={() => deleteRoutine(r.id)}
              onStart={() => startWorkout(r, navigate)}
            />
          ))}
        </div>
      </div>

      {showProgramForm && (
        <ProgramFormModal
          onCreate={createProgram}
          onClose={() => setShowProgramForm(false)}
        />
      )}
</div>
  );
}

async function startWorkout(routine, navigate) {
  try {
    const { data } = await axios.post(
      `${API_URL}/workouts/start`,
      { routine_id: routine.id },
      { withCredentials: true }
    );
    navigate(`/session/${data.id}`);
  } catch (e) {
    console.error(e);
    alert('Could not start workout');
  }
}

function ProgramSection({ program, routines, groups, onDelete, onDeleteRoutine, onStart }) {
  return (
    <div className="ios-card p-4 mb-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Folder size={18} weight="fill" className="text-userRed" />
          <h3 className="text-lg font-black font-heading uppercase text-graphite">
            {program.name}
          </h3>
          <span className="text-xs font-semibold text-graphite/50 tabular-nums">
            {routines.length}
          </span>
        </div>
      </div>
      {routines.length === 0 ? (
        <p className="text-xs text-graphite/40 italic">No routines in this program yet</p>
      ) : (
        <div className="space-y-2">
          {routines.map((r) => (
            <RoutineListRow
              key={r.id}
              routine={r}
              group={groups.find((g) => g.id === r.group_id)}
              onEdit={() => (window.location.href = `/routine/${r.id}/edit`)}
              onDelete={() => onDeleteRoutine(r.id)}
              onStart={() => onStart(r)}
              compact
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RoutineListRow({ routine, group, onEdit, onDelete, onStart, compact }) {
  const exerciseCount = routine.exercises?.length || 0;
  const totalSets = routine.exercises?.reduce((a, e) => a + (e.target_sets || 0), 0) || 0;
  return (
    <div
      className={`${compact ? '' : 'ios-card'} p-3 flex items-center gap-3`}
      data-testid={`routine-row-${routine.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          {group ? (
            <UsersThree size={12} weight="fill" className="text-partnerBlue" />
          ) : (
            <House size={12} weight="fill" className="text-graphite/40" />
          )}
          <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-graphite/50 truncate">
            {group ? group.name : 'Personal'}
          </p>
        </div>
        <p className="text-base font-black font-heading uppercase text-graphite truncate">
          {routine.name}
        </p>
        <p className="text-[10px] uppercase tracking-[0.15em] text-graphite/50 font-semibold">
          {exerciseCount} lifts · {totalSets} sets
        </p>
      </div>
      <button
        onClick={onStart}
        disabled={exerciseCount === 0}
        className="h-10 px-4 bg-userRed text-bone font-black text-xs uppercase tracking-[0.15em] rounded-ios-pill disabled:opacity-40"
        data-testid={`row-send-it-${routine.id}`}
      >
        Send It
      </button>
      <button
        onClick={onEdit}
        className="w-10 h-10 border border-graphite/30 text-graphite rounded-ios-pill flex items-center justify-center hover:bg-graphite hover:text-bone"
        data-testid={`row-edit-${routine.id}`}
        aria-label="Edit"
      >
        <PencilSimple size={14} weight="bold" />
      </button>
    </div>
  );
}

function ProgramFormModal({ onCreate, onClose }) {
  const [name, setName] = useState('');
  return (
    <div className="fixed inset-0 z-[1100] bg-graphite/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3">
      <div className="w-full max-w-md bg-bone rounded-ios-xl shadow-ios overflow-hidden animate-slide-up">
        <div className="px-5 py-5">
          <h2 className="text-2xl font-black font-heading uppercase text-graphite mb-4">
            New Program
          </h2>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Week 1, PPL, Cutting Phase"
            className="w-full h-14 px-4 rounded-ios-pill border border-hairline bg-boneWarm focus:outline-none focus:ring-2 focus:ring-graphite text-base mb-4"
            data-testid="program-name-input"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 h-14 border border-graphite/30 text-graphite font-bold text-sm uppercase tracking-[0.15em] rounded-ios-pill"
              data-testid="cancel-program-button"
            >
              Cancel
            </button>
            <button
              onClick={() => name.trim() && onCreate(name.trim())}
              disabled={!name.trim()}
              className="flex-1 h-14 bg-userRed text-bone font-bold text-sm uppercase tracking-[0.15em] rounded-ios-pill disabled:opacity-40"
              data-testid="create-program-button"
            >
              Create
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
